import React, { useEffect, useState, useCallback } from "react";
import api, { formatApiError } from "@/lib/api";
import AppShell from "@/components/AppShell";
import { PageContainer, PageHeader, EmptyState } from "@/components/Page";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { Plus, Trash2, Pencil, Link2 } from "lucide-react";
import { useAuth } from "@/context/AuthContext";

export default function Units() {
  const { user } = useAuth();
  const [units, setUnits] = useState([]);
  const [residents, setResidents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [assigning, setAssigning] = useState(null);
  const [assignResidentId, setAssignResidentId] = useState("");
  const [form, setForm] = useState({ block: "", house_number: "", apartment_number: "" });

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [u, r] = await Promise.all([
        api.get("/units"),
        api.get("/users", { params: { role: "resident" } }),
      ]);
      setUnits(u.data);
      setResidents(r.data);
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const submit = async (e) => {
    e.preventDefault();
    try {
      if (editing) {
        await api.patch(`/units/${editing.id}`, form);
        toast.success("Unit updated");
      } else {
        await api.post("/units", { ...form, estate_id: user.estate_id });
        toast.success("Unit created");
      }
      setOpen(false); setEditing(null);
      setForm({ block: "", house_number: "", apartment_number: "" });
      load();
    } catch (err) { toast.error(formatApiError(err)); }
  };

  const onDelete = async (id) => {
    if (!window.confirm("Delete this unit?")) return;
    try { await api.delete(`/units/${id}`); toast.success("Deleted"); load(); }
    catch (err) { toast.error(formatApiError(err)); }
  };

  const submitAssign = async () => {
    try {
      await api.post(`/units/${assigning.id}/assign`, { resident_id: assignResidentId || null });
      toast.success("Assignment saved");
      setAssigning(null); load();
    } catch (err) { toast.error(formatApiError(err)); }
  };

  if (!user?.estate_id) {
    return <AppShell><PageContainer>
      <PageHeader eyebrow="Estate Manager" title="Units" />
      <EmptyState title="No estate assigned" description="Ask your Super Admin to assign you to an estate before creating units." />
    </PageContainer></AppShell>;
  }

  return (
    <AppShell>
      <PageContainer>
        <PageHeader
          eyebrow="Estate Manager"
          title="Units"
          description="Blocks, houses, and apartments across your estate. Assign a resident to each unit."
          actions={
            <Button
              onClick={() => { setEditing(null); setForm({ block: "", house_number: "", apartment_number: "" }); setOpen(true); }}
              data-testid="create-unit-button"
              className="rounded-none bg-black hover:bg-zinc-800 h-10 px-4 uppercase tracking-wider text-xs font-semibold"
            >
              <Plus className="h-4 w-4" /> New Unit
            </Button>
          }
        />

        {loading ? <div className="label-eyebrow">Loading…</div> :
          units.length === 0 ? (
            <EmptyState title="No units yet" description="Create your first block or apartment." />
          ) : (
            <div className="bg-white border border-zinc-200 overflow-x-auto">
              <table className="w-full text-sm min-w-[720px]">
                <thead className="border-b border-zinc-200 bg-zinc-50">
                  <tr className="text-left">
                    <th className="p-4 label-eyebrow font-semibold">Block</th>
                    <th className="p-4 label-eyebrow font-semibold">House #</th>
                    <th className="p-4 label-eyebrow font-semibold">Apt #</th>
                    <th className="p-4 label-eyebrow font-semibold">Resident</th>
                    <th className="p-4 label-eyebrow font-semibold text-right">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {units.map((u) => (
                    <tr key={u.id} className="border-b border-zinc-100" data-testid={`unit-row-${u.id}`}>
                      <td className="p-4">{u.block || "—"}</td>
                      <td className="p-4 font-semibold">{u.house_number}</td>
                      <td className="p-4">{u.apartment_number || "—"}</td>
                      <td className="p-4">
                        {u.resident ? (
                          <div>
                            <div className="font-medium">{u.resident.name}</div>
                            <div className="text-xs text-zinc-500">{u.resident.email}</div>
                          </div>
                        ) : <span className="text-xs text-zinc-400 uppercase tracking-wider">Vacant</span>}
                      </td>
                      <td className="p-4">
                        <div className="flex items-center justify-end gap-2">
                          <button
                            onClick={() => { setAssigning(u); setAssignResidentId(u.resident_id || ""); }}
                            data-testid={`assign-resident-${u.id}`}
                            className="p-2 border border-zinc-200 hover:bg-black hover:text-white hover:border-black transition-colors" title="Assign resident"
                          ><Link2 className="h-3.5 w-3.5" /></button>
                          <button
                            onClick={() => { setEditing(u); setForm({ block: u.block || "", house_number: u.house_number, apartment_number: u.apartment_number || "" }); setOpen(true); }}
                            data-testid={`edit-unit-${u.id}`}
                            className="p-2 border border-zinc-200 hover:bg-black hover:text-white hover:border-black transition-colors"
                          ><Pencil className="h-3.5 w-3.5" /></button>
                          <button onClick={() => onDelete(u.id)}
                            data-testid={`delete-unit-${u.id}`}
                            className="p-2 border border-zinc-200 hover:bg-red-600 hover:text-white hover:border-red-600 transition-colors">
                            <Trash2 className="h-3.5 w-3.5" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

        <Dialog open={open} onOpenChange={(v) => { setOpen(v); if (!v) setEditing(null); }}>
          <DialogContent className="rounded-none border-zinc-300" data-testid="unit-form-dialog">
            <DialogHeader>
              <DialogTitle className="font-display text-2xl font-black tracking-tight">
                {editing ? "Edit Unit" : "New Unit"}
              </DialogTitle>
            </DialogHeader>
            <form onSubmit={submit} className="space-y-4">
              <div>
                <Label className="label-eyebrow">Block</Label>
                <Input value={form.block} onChange={(e) => setForm({ ...form, block: e.target.value })} data-testid="unit-block-input" className="rounded-none border-zinc-300 focus-visible:ring-0 focus-visible:border-black mt-2" placeholder="e.g. Block A" />
              </div>
              <div>
                <Label className="label-eyebrow">House Number</Label>
                <Input required value={form.house_number} onChange={(e) => setForm({ ...form, house_number: e.target.value })} data-testid="unit-house-input" className="rounded-none border-zinc-300 focus-visible:ring-0 focus-visible:border-black mt-2" placeholder="e.g. 12" />
              </div>
              <div>
                <Label className="label-eyebrow">Apartment Number</Label>
                <Input value={form.apartment_number} onChange={(e) => setForm({ ...form, apartment_number: e.target.value })} data-testid="unit-apt-input" className="rounded-none border-zinc-300 focus-visible:ring-0 focus-visible:border-black mt-2" placeholder="e.g. 3B" />
              </div>
              <DialogFooter>
                <Button type="submit" data-testid="submit-unit-button" className="rounded-none bg-black hover:bg-zinc-800 uppercase tracking-wider text-xs font-semibold">
                  {editing ? "Save Changes" : "Create Unit"}
                </Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>

        <Dialog open={!!assigning} onOpenChange={(v) => !v && setAssigning(null)}>
          <DialogContent className="rounded-none border-zinc-300" data-testid="assign-resident-dialog">
            <DialogHeader>
              <DialogTitle className="font-display text-2xl font-black tracking-tight">Assign Resident</DialogTitle>
            </DialogHeader>
            <div>
              <Label className="label-eyebrow">Resident</Label>
              <Select value={assignResidentId || "none"} onValueChange={(v) => setAssignResidentId(v === "none" ? "" : v)}>
                <SelectTrigger className="rounded-none border-zinc-300 mt-2" data-testid="assign-resident-select">
                  <SelectValue placeholder="Select a resident" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">— Unassign —</SelectItem>
                  {residents.map((r) => <SelectItem key={r.id} value={r.id}>{r.name} ({r.email})</SelectItem>)}
                </SelectContent>
              </Select>
              {residents.length === 0 && <div className="text-xs text-zinc-500 mt-2">Create residents first in the Residents page.</div>}
            </div>
            <DialogFooter>
              <Button onClick={submitAssign} data-testid="submit-assign-resident" className="rounded-none bg-black hover:bg-zinc-800 uppercase tracking-wider text-xs font-semibold">Save</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </PageContainer>
    </AppShell>
  );
}
